import { Action, Dispatch, bindActionCreators } from "redux";
import { ApplicationState, AppThunkAction } from "../../store";
import { ApprovalRequest, ApprovalSettingsApplier, ApprovalStoreState } from "../../store/ApprovalCenterStore";
import { isInReadonlyMode } from "../../store/User";
import { contains, CommonOperations } from "../../store/permissions";
import { EntityType, UserPreferencesSettingsUpdate } from "../../entities/common";
import * as FiltersStore from "../../store/filters";
import { FilterKeys } from "../../store/filters";
import * as Metadata from "../../entities/Metadata";

export type ApprovalTabSpec = {
    key: string;
    label: string;
    isVisible: (state: ApplicationState) => boolean;
    selectPendingCount: (state: ApplicationState) => number;
    settings: ApprovalSettingsApplier;
    loadPendingCount: () => AppThunkAction<Action>;
    render: () => JSX.Element;
};

export type ApprovalTabSettings = {
    activeFilter?: {
        filterId: string | null;
        preFilterId: string | null;
    };
    displayFields?: string[];
    approvalCenterActiveTab?: string;
    details?: { sortBy?: any; subViews?: any };
};

export type SaveApprovalTabSetting = {
    activeFilter: (value: { filterId: string | null; preFilterId: string | null }) => void;
    displayFields: (value: string[]) => void;
    approvalCenterActiveTab: (value: string) => void;
};

export const buildSaveApprovalTabSetting = (
    saveSettings: (update: UserPreferencesSettingsUpdate) => void
): SaveApprovalTabSetting => ({
    activeFilter: value => saveSettings({
        parentSettingsPathKeys: ["activeFilter"],
        valueBySettingNameMap: { filterId: value.filterId, preFilterId: value.preFilterId }
    }),
    displayFields: value => saveSettings({
        parentSettingsPathKeys: [],
        valueBySettingNameMap: { displayFields: value }
    }),
    approvalCenterActiveTab: value => saveSettings({
        parentSettingsPathKeys: [],
        valueBySettingNameMap: { approvalCenterActiveTab: value }
    })
});

export type ApprovalTabState<TDetails> = {
    requests: ApprovalRequest<TDetails>[];
    isLoading: boolean;
    isReadonlyMode: boolean;
    serverFields: Metadata.Field[];
    filters: Metadata.IFilter<Metadata.BaseFilterValue>[];
    activeFilter?: Metadata.IFilter<Metadata.BaseFilterValue>;
    canManageConfiguration: boolean;
    isSettingsLoading: boolean;
    settings: ApprovalTabSettings;
    preFilterId?: string;
    isAdmin: boolean;
    currentUserId: string;
};

export const selectApprovalTabState = <TDetails>(
    state: ApplicationState,
    approvalState: ApprovalStoreState<TDetails>,
    filterKey: FilterKeys
): ApprovalTabState<TDetails> => {
    const fieldsState = state.fields[EntityType.ApprovalRequest];
    const serverFields = fieldsState ? fieldsState.allIds.map(id => fieldsState.byId[id]) : [];
    const filtersState = FiltersStore.getFilter(state.filters, EntityType.ApprovalRequest, filterKey);
    const settings = (approvalState.settings ?? {}) as ApprovalTabSettings;
    const stored = settings.activeFilter;
    const activeFilter = (stored?.filterId
        ? filtersState.all.find(f => f.id === stored.filterId)
        : undefined)
        ?? filtersState.all.find(f => f.isDefault);

    return {
        requests: approvalState.requests,
        isLoading: approvalState.isLoading,
        isReadonlyMode: isInReadonlyMode(state.user, state.tenant),
        serverFields,
        filters: filtersState.all,
        activeFilter,
        canManageConfiguration: contains(state.user.permissions.common, CommonOperations.ConfigurationManage),
        isSettingsLoading: approvalState.isSettingsLoading,
        settings,
        preFilterId: stored?.preFilterId ?? undefined,
        isAdmin: contains(state.user.permissions.common, CommonOperations.Administrate),
        currentUserId: state.user.id ?? ""
    };
};

export type ApprovalTabActions<T> = {
    actions: T;
    filtersActions: ReturnType<typeof FiltersStore.actionCreators.forEntity>;
};

export const buildApprovalTabActions = <T>(actionCreators: T, filterKey: FilterKeys) =>
    (dispatch: Dispatch): ApprovalTabActions<T> => ({
        actions: bindActionCreators(actionCreators as any, dispatch) as T,
        filtersActions: bindActionCreators(
            FiltersStore.actionCreators.forEntity(EntityType.ApprovalRequest, filterKey),
            dispatch
        )
    });
